// src/services/reportDownload.service.ts
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import { authenticationService } from 'src/services/authentication.service';
import { get } from 'src/services/httpClient.service';

// Helper function to convert Blob to Base64 with timeout
const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const timeout = setTimeout(() => {
      reader.abort();
      reject(new Error('FileReader timeout'));
    }, 5000);

    reader.onloadend = () => {
      clearTimeout(timeout);
      resolve(reader.result as string);
    };
    reader.onerror = (error) => {
      clearTimeout(timeout);
      reject(error);
    };
    reader.readAsDataURL(blob);
  });
};

// Company info interface based on actual API response
interface CompanyInfo {
  registrationName: string;
  companyName: string;
  companyShortName: string;
  companyAddress?: string;
  companyCity?: string;
  companyState?: string;
  companyPincode?: string;
  companyPhone?: string;
  companyEmail?: string;
  companyGst?: string;
  vendorName?: string;
}

export interface ReportColumn {
  field: string;
  headerName: string;
  width?: number;
  flex?: number;
  minWidth?: number;
  type?: string;
  valueGetter?: (params: any) => any;
  renderCell?: (params: any) => any;
}

export interface DownloadOptions {
  title: string;
  fileName?: string;
  columns: ReportColumn[];
  rows: any[];
  month?: number;
  year?: number;
  // Excel specific
  excelHeaders?: string[];
  excelRowMapper?: (row: any) => any[];
  // Additional info for PDF (summary statistics)
  additionalInfo?: Record<string, any>;
}

class ReportDownloadService {
  private vendorId: string;
  private franchiseId: string;
  private companyInfo: CompanyInfo;
  private apiBaseUrl: string;
  private logoCache: { data: string | null; type: string; timestamp: number } | null = null;
  private readonly LOGO_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private readonly DEFAULT_COMPANY_NAME = 'IPTV Network';
  private readonly DEFAULT_COMPANY_SHORT_NAME = 'IPTV';

  constructor() {
    this.apiBaseUrl = process.env.REACT_APP_BASE_API_URL || '';
    
    // Initialize with safe defaults
    this.vendorId = '';
    this.franchiseId = '';
    this.companyInfo = this.getDefaultCompanyInfo();
    
    // Load data after initialization
    this.loadCompanyData();
  }

  /**
   * Get default company info when localStorage data is not available
   */
  private getDefaultCompanyInfo(): CompanyInfo {
    return {
      registrationName: this.DEFAULT_COMPANY_NAME,
      companyName: this.DEFAULT_COMPANY_NAME,
      companyShortName: this.DEFAULT_COMPANY_SHORT_NAME,
      companyAddress: '',
      companyCity: '',
      companyState: '',
      companyPincode: '',
      companyPhone: '',
      companyEmail: '',
      companyGst: '',
      vendorName: ''
    };
  }

  /**
   * Safely get item from localStorage with fallback
   */
  private safeLocalStorageGet(key: string, defaultValue: string = ''): string {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const value = localStorage.getItem(key);
        return value !== null && value !== undefined ? value : defaultValue;
      }
    } catch (error) {
      console.warn(`Error reading ${key} from localStorage:`, error);
    }
    return defaultValue;
  }

  /**
   * Load company data from localStorage with proper error handling
   */
  private loadCompanyData(): void {
    try {
      // Get vendor/franchise IDs
      try {
        const ids = authenticationService.getVendorFranchiseIds();
        console.log("ids:::", ids);
        this.vendorId = ids?.vendorId || '';
        this.franchiseId = ids?.franchiseId || '';
      } catch (error) {
        console.error('Error getting vendor/franchise IDs:', error);
        this.vendorId = '';
        this.franchiseId = '';
      }

      // Safely get all localStorage items with fallbacks
      const registrationName = this.safeLocalStorageGet('registrationName');
      const companyShortName = this.safeLocalStorageGet('companyShortName');
      const companyName = this.safeLocalStorageGet('companyName');
      const companyEmail = this.safeLocalStorageGet('companyEmail');
      const companyPhone = this.safeLocalStorageGet('companyPhone');
      const companyCity = this.safeLocalStorageGet('companyCity');
      const companyState = this.safeLocalStorageGet('companyState');
      const companyAddress = this.safeLocalStorageGet('companyAddress');
      const companyPincode = this.safeLocalStorageGet('companyPincode');
      const companyGst = this.safeLocalStorageGet('companyGst');
      const vendorName = this.safeLocalStorageGet('vendorName');

      // Check if we have at least some company info
      const hasCompanyInfo = registrationName || companyName || companyAddress;

      if (hasCompanyInfo) {
        this.companyInfo = {
          registrationName: registrationName || this.companyInfo.registrationName,
          companyName: companyName || this.companyInfo.companyName,
          companyShortName: companyShortName || this.companyInfo.companyShortName,
          companyAddress: companyAddress || this.companyInfo.companyAddress,
          companyCity: companyCity || this.companyInfo.companyCity,
          companyState: companyState || this.companyInfo.companyState,
          companyPincode: companyPincode || this.companyInfo.companyPincode,
          companyPhone: companyPhone || this.companyInfo.companyPhone,
          companyEmail: companyEmail || this.companyInfo.companyEmail,
          companyGst: companyGst || this.companyInfo.companyGst,
          vendorName: vendorName || this.companyInfo.vendorName
        };
      } else {
        console.log('No company info found in localStorage, using defaults');
        // Keep default company info
      }
    } catch (error) {
      console.error('Error loading company data:', error);
      // Keep default company info on error
    }
  }

  /**
   * Public method to update company info (call this after login/data load)
   */
  public updateCompanyInfo(info?: Partial<CompanyInfo>): void {
    try {
      if (info) {
        // Update with provided info
        this.companyInfo = {
          ...this.companyInfo,
          ...info
        };
      } else {
        // Reload from localStorage
        this.loadCompanyData();
      }
      console.log('Company info updated:', this.companyInfo);
    } catch (error) {
      console.error('Error updating company info:', error);
    }
  }

  /**
   * Check if company info is populated with real data
   */
  private hasValidCompanyInfo(): boolean {
    return !!(this.companyInfo.registrationName && 
              this.companyInfo.registrationName !== this.DEFAULT_COMPANY_NAME) ||
           !!(this.companyInfo.companyName && 
              this.companyInfo.companyName !== this.DEFAULT_COMPANY_NAME);
  }

  // Format company address for display with safety checks
  private formatCompanyAddress(): string {
    const {
      companyAddress = '',
      companyCity = '',
      companyState = '',
      companyPincode = ''
    } = this.companyInfo;

    // Clean up the address parts
    const cleanAddress = companyAddress 
      ? companyAddress.replace(/\n/g, ', ').replace(/\s+/g, ' ').trim()
      : '';
    
    const parts = [
      cleanAddress,
      companyCity?.trim(),
      companyState?.trim(),
      companyPincode?.trim()
    ].filter(part => part && part.length > 0 && part !== 'null' && part !== 'undefined');

    return parts.length > 0 ? parts.join(', ') : '';
  }

  // Get company header for display
  private getCompanyHeader(): string {
    // Try to get from registrationName or companyName first
    if (this.companyInfo.registrationName && 
        this.companyInfo.registrationName.trim() && 
        this.companyInfo.registrationName !== this.DEFAULT_COMPANY_NAME) {
      return this.companyInfo.registrationName;
    }
    
    if (this.companyInfo.companyName && 
        this.companyInfo.companyName.trim() && 
        this.companyInfo.companyName !== this.DEFAULT_COMPANY_NAME) {
      return this.companyInfo.companyName;
    }
    
    // Try to extract from address first line
    if (this.companyInfo.companyAddress && this.companyInfo.companyAddress.trim()) {
      const addressLines = this.companyInfo.companyAddress.split('\n');
      if (addressLines.length > 0 && addressLines[0].trim()) {
        return addressLines[0].trim();
      }
    }
    
    return this.DEFAULT_COMPANY_NAME;
  }

  // Fetch logo with caching and timeout
  private async fetchLogo(): Promise<{ data: string | null; type: string }> {
    // Check cache first
    if (this.logoCache && (Date.now() - this.logoCache.timestamp) < this.LOGO_CACHE_DURATION) {
      return { data: this.logoCache.data, type: this.logoCache.type };
    }

    if (!this.vendorId) {
      console.log('No vendor ID available for logo fetch');
      return { data: null, type: 'PNG' };
    }

    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    try {
      const timestamp = new Date().getTime();
      const logoUrl = `${this.apiBaseUrl}/api/application-config/vendor/${this.vendorId}/getLogoImage?t=${timestamp}`;
      
      console.log('Fetching logo fromm:', logoUrl);
      const token = localStorage.getItem('token');

      const logoResponse = await fetch(logoUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (logoResponse.ok && logoResponse.status === 200) {
        const blob = await logoResponse.blob();
        
        if (blob && blob.size > 0) {
          // Check if it's a valid image
          if (!blob.type.startsWith('image/')) {
            console.warn('Fetched logo is not an image:', blob.type);
            return { data: null, type: 'PNG' };
          }

          const logoBase64 = await blobToBase64(blob);
          
          // Detect image type
          let logoType = 'PNG';
          if (blob.type.includes('jpeg') || blob.type.includes('jpg')) {
            logoType = 'JPEG';
          } else if (blob.type.includes('png')) {
            logoType = 'PNG';
          } else if (blob.type.includes('svg')) {
            logoType = 'SVG';
          } else if (blob.type.includes('gif')) {
            logoType = 'GIF';
          } else if (blob.type.includes('bmp')) {
            logoType = 'BMP';
          }
          
          // Cache the result
          this.logoCache = {
            data: logoBase64,
            type: logoType,
            timestamp: Date.now()
          };
          
          console.log('Logo loaded successfully:', logoType);
          return { data: logoBase64, type: logoType };
        }
      } else {
        console.warn(`Logo fetch failed with status: ${logoResponse.status}`);
      }
    } catch (logoError: any) {
      clearTimeout(timeoutId);
      
      // Handle abort/timeout errors gracefully
      if (logoError.name === 'AbortError') {
        console.warn('Logo fetch timeout - continuing without logo');
      } else {
        console.warn('Could not load logo:', logoError.message || logoError);
      }
    }

    return { data: null, type: 'PNG' };
  }

  

  // Main download method
  async downloadReport(options: DownloadOptions, format: 'pdf' | 'excel' | 'both' = 'both'): Promise<boolean> {
    // Ensure company info is fresh before download
    this.refreshCompanyInfoIfNeeded();
    
    const timestamp = dayjs().format('DDMMYYYY_HHmmss');
    const defaultFileName = `${options.title.replace(/\s+/g, '_')}_${timestamp}`;

    const promises = [];

    if (format === 'pdf' || format === 'both') {
      promises.push(
        this.generatePDF({
          ...options,
          fileName: options.fileName || `${defaultFileName}.pdf`
        })
      );
    }

    if (format === 'excel' || format === 'both') {
      promises.push(
        this.generateExcel({
          ...options,
          fileName: options.fileName || `${defaultFileName}.xlsx`
        })
      );
    }

    const results = await Promise.allSettled(promises);
    return results.every(result => result.status === 'fulfilled' && result.value === true);
  }

  /**
   * Refresh company info if it appears to be missing
   */
  private refreshCompanyInfoIfNeeded(): void {
    // Check if we have meaningful company info
    const hasRealData = this.hasValidCompanyInfo();
    
    if (!hasRealData) {
      console.log('Company info appears to be default, attempting refresh...');
      this.loadCompanyData();
    }
  }

  // PDF Generation - WITH COMPANY INFO ON RIGHT SIDE
  private async generatePDF(options: DownloadOptions): Promise<boolean> {
    const {
      title,
      columns,
      rows,
      month,
      year,
      fileName,
      additionalInfo = {}
    } = options;

    try {
      // Validate inputs
      if (!title) {
        console.error('PDF generation failed: No title provided');
        return false;
      }

      const doc = new jsPDF('landscape', 'pt', 'a4');
      
      // PDF styling variables
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const marginLeft = 40;
      const marginRight = 40;
      let currentY = 30;
      
      // Try to fetch logo with safety checks
      let hasLogo = false;
      let logoData: { data: string | null; type: string } = { data: null, type: 'PNG' };
      
      try {
        logoData = await this.fetchLogo();
        hasLogo = !!logoData.data;
      } catch (logoError) {
        console.warn('Logo fetch error, continuing without logo:', logoError);
      }
      
      // Add header border
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(1);
      doc.line(marginLeft, currentY - 10, pageWidth - marginRight, currentY - 10);
      
      // LEFT SIDE: Logo and Title
      let leftColumnX = marginLeft;
      //let rightColumnX = pageWidth - marginRight - 280; // Space for company info

      // Reserve fixed width for right section
const rightSectionWidth = 250;
const rightColumnX = pageWidth - marginRight - rightSectionWidth;

// Title max width = space between logo and right section
const titleMaxWidth = rightColumnX - (leftColumnX + (hasLogo ? 140 : 0)) - 10;
      
      if (hasLogo && logoData.data) {
        try {
          // Add logo on left side
          const logoWidth = 120;
          const logoHeight = 60;
          
          // Validate image before adding
          doc.addImage(logoData.data, logoData.type, leftColumnX, currentY, logoWidth, logoHeight);
          
          // Add report title next to logo
          doc.setFontSize(18);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(33, 33, 33);
          //doc.text(title, leftColumnX + logoWidth + 15, currentY + 25);
    

              const titleX = leftColumnX + (hasLogo ? 140 : 0);
                const wrappedTitle = doc.splitTextToSize(title, titleMaxWidth);

              doc.text(wrappedTitle, titleX, currentY + 25);
        } catch (imageError) {
          console.warn('Error adding logo image, using fallback:', imageError);
          hasLogo = false;
        }
      }
      
      if (!hasLogo) {
        // Use fallback: Draw a colored rectangle with text
        
        try {
          doc.setFillColor(33, 150, 243);
          doc.rect(leftColumnX, currentY, 120, 40, 'F');
          
          // Add placeholder text
          doc.setFontSize(14);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(255, 255, 255);
          doc.text('LOGO', leftColumnX + 60, currentY + 20, { align: 'center' });
          
          doc.setFontSize(10);
          doc.text('Placeholder', leftColumnX + 60, currentY + 35, { align: 'center' });
          
          // Add report title next to fallback logo
          doc.setFontSize(18);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(33, 33, 33);
          doc.text(title, leftColumnX + 140, currentY + 25);
        } catch (fallbackError) {
          // If even fallback fails, just add title
          console.warn('Fallback logo failed, adding title only:', fallbackError);
          doc.setFontSize(18);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(33, 33, 33);
          doc.text(title, leftColumnX, currentY + 25);
        }
      }
      
      // RIGHT SIDE: Company Information
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(60, 60, 60);
      
      // Get company header
      const companyHeader = this.getCompanyHeader();
      doc.text(companyHeader, rightColumnX, currentY + 5);
      
      // Company details
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(80, 80, 80);
      
      let detailsY = currentY + 20;
      const lineHeight = 12;
      
      // Address - format it properly
      const fullAddress = this.formatCompanyAddress();
      if (fullAddress && fullAddress.length > 0) {
        // Split long address into multiple lines if needed
        const addressLines = this.splitTextIntoLines(fullAddress, 55);
        addressLines.forEach((line, index) => {
          if (line && line.trim() && line !== 'null' && line !== 'undefined') {
            doc.text(line, rightColumnX, detailsY + (index * lineHeight));
          }
        });
        detailsY += (addressLines.length * lineHeight);
      }
      
      // Phone
      if (this.companyInfo.companyPhone && 
          this.companyInfo.companyPhone.trim() && 
          this.companyInfo.companyPhone !== 'null') {
        doc.text(`Phone : ${this.companyInfo.companyPhone}`, rightColumnX, detailsY);
        detailsY += lineHeight;
      }
      
      // Email
      if (this.companyInfo.companyEmail && 
          this.companyInfo.companyEmail.trim() && 
          this.companyInfo.companyEmail !== 'null') {
        doc.text(`E-mail : ${this.companyInfo.companyEmail}`, rightColumnX, detailsY);
        detailsY += lineHeight;
      }
      
      // GST
      if (this.companyInfo.companyGst && 
          this.companyInfo.companyGst.trim() && 
          this.companyInfo.companyGst !== 'null') {
        // Clean up GST (remove extra spaces)
        const cleanGst = this.companyInfo.companyGst.trim();
        doc.text(`GST# : ${cleanGst}`, rightColumnX, detailsY);
      }
      
      // Update currentY based on the taller of the two sections
      const leftHeight = hasLogo ? 60 : 80;
      const rightHeight = (detailsY - currentY) + 20;
      currentY += Math.max(leftHeight, rightHeight) + 10;
      
      // Report details section
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      const monthName = month ? monthNames[month - 1] : '';
      const generatedAt = dayjs().format('DD/MM/YYYY HH:mm:ss'); // CHANGED: from DD-MMM-YYYY to DD/MM/YYYY
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      
      // Create details box
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.5);
      doc.rect(marginLeft, currentY, pageWidth - marginLeft - marginRight, 40);
      
      // Add details text
      doc.text(`Report Period: ${monthName} ${year || ''}`, marginLeft + 10, currentY + 15);
      doc.text(`Generated: ${generatedAt}`, marginLeft + 10, currentY + 30);
      
      // Vendor info with fallback
      const vendorDisplay = this.companyInfo.vendorName || 
                           this.companyInfo.companyShortName || 
                           this.companyInfo.registrationName || 
                           'N/A';
      doc.text(`Vendor: ${vendorDisplay}`, pageWidth - marginRight - 150, currentY + 15);
      doc.text(`Total Records: ${rows?.length || 0}`, pageWidth - marginRight - 150, currentY + 30);
      
      currentY += 60;
      
      // ADDITIONAL INFO (Summary Statistics)
      if (additionalInfo && Object.keys(additionalInfo).length > 0) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(60, 60, 60);
        doc.text('Summary:', marginLeft + 10, currentY);
        
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(80, 80, 80);
        
        let infoX = marginLeft + 80;
        Object.entries(additionalInfo).forEach(([key, value], index) => {
          // Ensure we don't go off page
          if (infoX < pageWidth - marginRight - 100) {
            doc.text(`${key}: ${value}`, infoX, currentY);
            infoX += 100;
          }
        });
        
        currentY += 20;
      }
      
      // Prepare table data
      const headers = columns?.map(col => col.headerName) || [];
      
      // Prepare table rows
      const tableRows = (rows || []).map(row =>
        (columns || []).map(col => {
          try {
            // Use valueGetter if available
            if (col.valueGetter) {
              const value = col.valueGetter({ row });
              return value !== null && value !== undefined ? String(value) : '';
            }
            
            const value = row[col.field];
            
            // Format dates if needed - CHANGED to DD/MM/YYYY format
            if (col.field?.toLowerCase().includes('date') || col.field?.toLowerCase().includes('time')) {
              if (value) {
                if (typeof value === 'string' && value.includes('.')) {
                  return value.split('.')[0];
                }
                try {
                  return dayjs(value).isValid() ? dayjs(value).format('DD/MM/YYYY HH:mm:ss') : String(value);
                } catch {
                  return String(value);
                }
              }
              return '';
            }
            
            // Handle arrays
            if (Array.isArray(value)) {
              return value.join(', ');
            }
            
            // Truncate long strings for PDF display only
            if (typeof value === 'string' && value.length > 50) {
              return value.substring(0, 47) + '...';
            }
            
            return value !== null && value !== undefined ? String(value) : '';
          } catch (error) {
            console.warn('Error formatting cell value:', error);
            return '';
          }
        })
      );
      
      // Add table with autoTable
      autoTable(doc, {
        head: [headers],
        body: tableRows,
        startY: currentY,
        margin: { left: marginLeft, right: marginRight },
        styles: { 
          fontSize: 7,
          cellPadding: 3,
          overflow: 'linebreak',
          lineColor: [220, 220, 220],
          lineWidth: 0.3
        },
        headStyles: { 
          fillColor: [33, 150, 243],
          textColor: [255, 255, 255],
          fontSize: 8,
          fontStyle: 'bold',
          halign: 'center'
        },
        alternateRowStyles: {
          fillColor: [248, 248, 248]
        },
        didDrawPage: (data) => {
          try {
            // Footer
            const pageCount = doc.getNumberOfPages();
            doc.setFontSize(8);
            doc.setTextColor(150, 150, 150);
            
            // Page number
            doc.text(
              `Page ${data.pageNumber} of ${pageCount}`,
              pageWidth / 2,
              pageHeight - 20,
              { align: 'center' }
            );
            
            // Company name in footer
            const footerCompanyName = this.companyInfo.companyShortName || 
                                      this.companyInfo.registrationName || 
                                      companyHeader || 
                                      this.DEFAULT_COMPANY_NAME;
            
            doc.text(
              `© ${new Date().getFullYear()} ${footerCompanyName} - Confidential Report`,
              pageWidth / 2,
              pageHeight - 10,
              { align: 'center' }
            );
          } catch (footerError) {
            console.warn('Error drawing footer:', footerError);
          }
        }
      });

      doc.save(fileName || `${title.replace(/\s+/g, '_')}.pdf`);
      return true;
    } catch (error) {
      console.error('Error generating PDF:', error);
      
      // Fallback to basic PDF
      return this.generateFallbackPDF(options);
    }
  }

  // Fallback PDF generation
  private generateFallbackPDF(options: DownloadOptions): boolean {
    const {
      title,
      columns,
      rows,
      month,
      year,
      fileName
    } = options;

    try {
      const fallbackDoc = new jsPDF('landscape', 'pt', 'a4');
      
      // Add basic header
      fallbackDoc.setFontSize(14);
      fallbackDoc.setFont('helvetica', 'bold');
      fallbackDoc.text(`${title}`, 14, 20);
      
      fallbackDoc.setFontSize(10);
      fallbackDoc.setFont('helvetica', 'normal');
      fallbackDoc.text(`Period: ${month || ''} ${year || ''}`, 14, 35);
      fallbackDoc.text(`Generated: ${dayjs().format('DD/MM/YYYY HH:mm:ss')}`, 14, 45); // CHANGED: from DD-MMM-YYYY to DD/MM/YYYY
      
      // Add company info
      fallbackDoc.setFontSize(8);
      const companyHeader = this.getCompanyHeader();
      fallbackDoc.text(companyHeader, 14, 55);
      
      // Prepare table data
      const headers = columns?.map(col => col.headerName) || [];
      const tableRows = (rows || []).map(row =>
        (columns || []).map(col => {
          try {
            if (col.valueGetter) {
              const value = col.valueGetter({ row });
              return value !== null && value !== undefined ? String(value) : '';
            }
            const value = row[col.field];
            
            // Format dates if needed - CHANGED to DD/MM/YYYY format
            if (col.field?.toLowerCase().includes('date') || col.field?.toLowerCase().includes('time')) {
              if (value) {
                if (typeof value === 'string' && value.includes('.')) {
                  return value.split('.')[0];
                }
                try {
                  return dayjs(value).isValid() ? dayjs(value).format('DD/MM/YYYY HH:mm:ss') : String(value);
                } catch {
                  return String(value);
                }
              }
              return '';
            }
            
            return value !== null && value !== undefined ? String(value) : '';
          } catch {
            return '';
          }
        })
      );

      autoTable(fallbackDoc, {
        head: [headers],
        body: tableRows,
        startY: 65,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [33, 150, 243] },
      });

      fallbackDoc.save(fileName || `${title.replace(/\s+/g, '_')}.pdf`);
      return true;
    } catch (fallbackError) {
      console.error('Even fallback PDF failed:', fallbackError);
      return false;
    }
  }

  // Helper function to split text into lines for PDF
  private splitTextIntoLines(text: string, maxCharsPerLine: number): string[] {
    if (!text || text === 'null' || text === 'undefined') return [];
    
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
      if (!word || word === 'null' || word === 'undefined') continue;
      
      if ((currentLine + ' ' + word).length <= maxCharsPerLine) {
        currentLine = currentLine ? currentLine + ' ' + word : word;
      } else {
        if (currentLine) {
          lines.push(currentLine);
        }
        currentLine = word;
      }
    }
    
    if (currentLine) {
      lines.push(currentLine);
    }
    
    return lines;
  }

  // Excel Generation
  private generateExcel(options: DownloadOptions): boolean {
    const {
      title,
      rows,
      fileName,
      excelHeaders,
      excelRowMapper,
      columns
    } = options;

    try {
      // Validate inputs
      if (!title) {
        console.error('Excel generation failed: No title provided');
        return false;
      }

      // Use custom headers if provided, otherwise generate from columns
      const headers = excelHeaders || (columns?.map(col => col.headerName) || []);
      
      // Use custom row mapper if provided, otherwise generate from columns
      const dataForExcel = excelRowMapper 
        ? (rows || []).map(excelRowMapper)
        : (rows || []).map(row =>
            (columns || []).map(col => {
              try {
                if (col.valueGetter) {
                  const value = col.valueGetter({ row });
                  return value !== null && value !== undefined ? value : '';
                }
                
                // Format dates properly - CHANGED to DD/MM/YYYY format
                if (col.field?.toLowerCase().includes('date') || col.field?.toLowerCase().includes('time')) {
                  const value = row[col.field];
                  if (value) {
                    if (typeof value === 'string' && value.includes('.')) {
                      return value.split('.')[0];
                    }
                    try {
                      return dayjs(value).isValid() ? dayjs(value).format('DD/MM/YYYY HH:mm:ss') : value;
                    } catch {
                      return value;
                    }
                  }
                  return '';
                }
                
                const value = row[col.field];
                if (Array.isArray(value)) {
                  return value.join(', ');
                }
                return value !== null && value !== undefined ? value : '';
              } catch (error) {
                console.warn('Error mapping Excel row:', error);
                return '';
              }
            })
          );

      const worksheetData = [headers, ...dataForExcel];
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

      // Auto-size columns with safety limits
      const colWidths = headers.map((header, idx) => {
        try {
          const maxLength = Math.max(
            String(header || '').length,
            ...dataForExcel.map(row => {
              const val = row[idx];
              return val ? String(val).length : 0;
            })
          );
          return { wch: Math.min(maxLength + 5, 60) };
        } catch {
          return { wch: 15 }; // Default width
        }
      });
      worksheet['!cols'] = colWidths;

      const workbook = XLSX.utils.book_new();
      const sheetName = title.substring(0, 31).replace(/[\[\]:\*\?\/\\]/g, ''); // Remove invalid characters
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName || 'Sheet1');

      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const safeFileName = fileName || `${title.replace(/[^a-zA-Z0-9_]/g, '_')}.xlsx`;
      saveAs(data, safeFileName);
      return true;
    } catch (error) {
      console.error('Error generating Excel:', error);
      return false;
    }
  }

  // Clear logo cache
  clearLogoCache(): void {
    this.logoCache = null;
  }

  // Refresh company info from localStorage
  refreshCompanyInfo(): void {
    this.loadCompanyData();
  }

  /**
   * Get current company info
   */
  getCompanyInfo(): CompanyInfo {
    return { ...this.companyInfo };
  }
}

export default new ReportDownloadService();